import { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMessage, 
  faSpinner, 
  faCode, 
  faFileCode,
  faChevronRight,
  faCopy, 
  faCheck, 
  faSearch, 
  faSyncAlt, 
  faTimes, 
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { getToken } from '../../function/token';
import domain from '../../../reusecomponent/domain';
import { ErrorToastContainer } from '../../function/CustomFunction';
import Preloader from '../preloader';

const GMB_GREEN = '#075E54';
const GMB_GREEN_LIGHT = '#128C7E';

const deliveryStatusMap = {
  0: { label: "Pending", bg: "#fef3c7", text: "#92400e", border: "#fde68a" },
  1: { label: "Sent", bg: "#dbeafe", text: "#1e40af", border: "#bfdbfe" },
  2: { label: "Delivered", bg: "#e0e7ff", text: "#3730a3", border: "#c7d2fe" },
  3: { label: "Read", bg: "#dcfce7", text: "#166534", border: "#bbf7d0" },
  4: { label: "Failed", bg: "#fee2e2", text: "#991b1b", border: "#fecaca" },
  5: { label: "Deleted", bg: "#f3f4f6", text: "#4b5563", border: "#e5e7eb" },
  6: { label: "Technical Error", bg: "#ffedd5", text: "#9a3412", border: "#fed7aa" }
};

// Deep parse any nested stringified JSON objects
const deepParseJson = (data) => {
  if (data === null || data === undefined) return null;
  if (typeof data === 'object') {
    if (Array.isArray(data)) {
      return data.map(deepParseJson);
    }
    const result = {};
    for (const key of Object.keys(data)) {
      result[key] = deepParseJson(data[key]);
    }
    return result;
  }
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        const parsed = JSON.parse(trimmed);
        return deepParseJson(parsed);
      } catch {
        return data;
      }
    }
  }
  return data;
};

// Syntax highlighter matching exact dark theme colors from design
const highlightJson = (jsonString) => {
  if (!jsonString) return '<span style="color: #94a3b8;">No data available</span>';
  const escaped = jsonString
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const jsonRegex = /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g;

  return escaped.replace(jsonRegex, (match) => {
    if (/^"/.test(match)) {
      if (/:$/.test(match)) {
        // Key with colon (e.g. "status":) -> Hot Pink Key + White Colon
        const keyPart = match.slice(0, -1);
        return `<span style="color: #f43f68; font-weight: 600;">${keyPart}</span><span style="color: #f8fafc;">:</span>`;
      }
      // String value -> Lime Yellow-Green
      return `<span style="color: #bef264; font-weight: 400;">${match}</span>`;
    }
    if (/true|false/.test(match)) {
      // Boolean -> Slate/Indigo Purple
      return `<span style="color: #818cf8; font-weight: 600;">${match}</span>`;
    }
    if (/null/.test(match)) {
      // Null -> Slate Gray
      return `<span style="color: #94a3b8; font-weight: 600;">${match}</span>`;
    }
    // Number -> Warm Orange
    return `<span style="color: #fb923c; font-weight: 500;">${match}</span>`;
  });
};

const SendingMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal State
  const [modalData, setModalData] = useState({
    isOpen: false,
    title: '',
    type: '', // 'Request' | 'Response'
    parsedObj: null,
    formattedStr: '',
    rawContent: '',
    phone: '',
    messageId: '',
    date: '',
    time: ''
  });
  const [copied, setCopied] = useState(false);

  const [apiPage, setApiPage] = useState(1);
  const [batchPage, setBatchPage] = useState(1);

  // Table container ref for scroll handling
  const scrollContainerRef = useRef(null);

  // Fetch messages from backend (auto-batches to 100 items even if server returns 50 per page)
  const fetchMessages = useCallback(async (startApiPage = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const token = getToken();
      let accumulated = [];
      let currentApiPage = startApiPage;
      let latestNextUrl = null;

      // Loop to fetch up to 100 items
      while (accumulated.length < 100) {
        const apiUrl = `${domain()}/api-messages?page=${currentApiPage}&per_page=100`;
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const result = await response.json();

        if (result.status) {
          const items = result.data?.data || [];
          latestNextUrl = result.data?.next_page_url || null;
          accumulated = [...accumulated, ...items];

          // If no next page or no items, stop
          if (!latestNextUrl || items.length === 0) {
            break;
          }
          currentApiPage++;
        } else {
          ErrorToastContainer(result.message || 'Failed to fetch messages');
          break;
        }
      }

      if (isLoadMore) {
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.message_id || m.id));
          const filteredNew = accumulated.filter(m => !existingIds.has(m.message_id || m.id));
          return [...prev, ...filteredNew];
        });
        setBatchPage(prev => prev + 1);
      } else {
        setMessages(accumulated);
        setBatchPage(1);
      }

      setNextPageUrl(latestNextUrl);
      setHasMore(!!latestNextUrl);
      setApiPage(currentApiPage);
    } catch (error) {
      console.error('Error fetching messages:', error);
      ErrorToastContainer('Network error. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setInitialLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchMessages(1, false);
  }, [fetchMessages]);

  // Load next batch of 100
  const loadNextPage = () => {
    if (loadingMore || loading || !hasMore) return;
    fetchMessages(apiPage, true);
  };

  // Scroll listener on the fixed height table container
  const handleContainerScroll = () => {
    if (!scrollContainerRef.current || loadingMore || loading || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 60) {
      loadNextPage();
    }
  };

  // Open Modal for Request or Response
  const handleOpenModal = (message, type) => {
    const raw = type === 'Request' ? message.body : message.response;
    const parsed = deepParseJson(raw);
    const formatted = typeof parsed === 'object' ? JSON.stringify(parsed, null, 2) : String(raw);

    setModalData({
      isOpen: true,
      title: type === 'Request' ? 'Request Payload' : 'API Response Data',
      type,
      parsedObj: parsed,
      formattedStr: formatted,
      rawContent: raw,
      phone: message.wa_id || 'N/A',
      messageId: message.message_id || 'N/A',
      date: message.date || '',
      time: message.time || ''
    });
    setCopied(false);
  };

  const handleCloseModal = () => {
    setModalData(prev => ({ ...prev, isOpen: false }));
  };

  const handleCopyJson = () => {
    if (modalData.formattedStr) {
      navigator.clipboard.writeText(modalData.formattedStr);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Filter messages based on search term and status
  const filteredMessages = messages.filter(msg => {
    const matchesSearch = 
      (msg.wa_id && msg.wa_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (msg.message_id && msg.message_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (msg.body && msg.body.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = 
      statusFilter === 'all' || 
      String(msg.message_deliver) === String(statusFilter);

    return matchesSearch && matchesStatus;
  });

  if (initialLoading) {
    return <Preloader action={true} />;
  }

  return (
    <div style={{ padding: '4px 8px 12px 8px', maxWidth: '100%', height: 'calc(100vh - 90px)', display: 'flex', flexDirection: 'column' }}>
      
      {/* Top Header Card */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '14px 20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        marginBottom: '10px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        flexShrink: 0
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: '#e8f5e9',
              color: GMB_GREEN,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '15px'
            }}>
              <FontAwesomeIcon icon={faMessage} />
            </div>
            <div>
              <h5 style={{ margin: 0, fontWeight: 700, color: '#1e293b', fontSize: '17px' }}>
                Message History
              </h5>
              <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                View all API sent messages, request payloads, and delivery responses (100 items per page)
              </p>
            </div>
          </div>
        </div>

        {/* Controls: Search, Filter, Page Info & Reload */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Search box */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '6px 12px',
            gap: '8px',
            minWidth: '200px'
          }}>
            <FontAwesomeIcon icon={faSearch} style={{ color: '#94a3b8', fontSize: '13px' }} />
            <input
              type="text"
              placeholder="Search phone, message ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: '13px',
                width: '100%',
                color: '#334155'
              }}
            />
            {searchTerm && (
              <FontAwesomeIcon
                icon={faTimes}
                style={{ color: '#94a3b8', cursor: 'pointer', fontSize: '12px' }}
                onClick={() => setSearchTerm('')}
              />
            )}
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '6px 10px',
              fontSize: '13px',
              color: '#334155',
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Statuses</option>
            <option value="1">Sent (1)</option>
            <option value="2">Delivered (2)</option>
            <option value="3">Read (3)</option>
            <option value="4">Failed (4)</option>
            <option value="0">Pending (0)</option>
          </select>

          {/* Stats Badge */}
          <div style={{
            background: '#f1f5f9',
            padding: '6px 12px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: 600,
            color: '#475569',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span>Total Loaded: <strong style={{ color: GMB_GREEN }}>{messages.length}</strong></span>
            <span>•</span>
            <span>Batch: <strong>{batchPage}</strong></span>
          </div>

          {/* Reload Button */}
          <button
            onClick={() => fetchMessages(1, false)}
            disabled={loading}
            title="Reload Messages"
            style={{
              background: loading ? '#e2e8f0' : GMB_GREEN,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '7px 14px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'background 0.2s'
            }}
          >
            <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
            Refresh
          </button>
        </div>
      </div>

      {/* Main Table Card with Fixed Height & Scrollable Area */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden'
      }}>
        
        {/* Scrollable Table Viewport */}
        <div
          ref={scrollContainerRef}
          onScroll={handleContainerScroll}
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'auto',
            position: 'relative'
          }}
        >
          {filteredMessages.length === 0 && !loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <FontAwesomeIcon icon={faMessage} size="3x" style={{ color: '#cbd5e1', marginBottom: '14px' }} />
              <h5 style={{ color: '#64748b', fontWeight: 600 }}>No messages found</h5>
              <p style={{ color: '#94a3b8', fontSize: '13px' }}>
                {searchTerm || statusFilter !== 'all' ? 'Try adjusting your search filter' : 'No message history available'}
              </p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
              {/* Sticky Table Header */}
              <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#f8fafc', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <tr style={{ borderBottom: '1.5px solid #e2e8f0', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <th style={{ padding: '12px 14px', width: '60px', textAlign: 'center' }}>#</th>
                  <th style={{ padding: '12px 14px', width: '140px' }}>Phone</th>
                  <th style={{ padding: '12px 14px', width: '220px' }}>Message ID</th>
                  <th style={{ padding: '12px 14px', width: '120px' }}>Status</th>
                  <th style={{ padding: '12px 14px', width: '150px' }}>Date & Time</th>
                  <th style={{ padding: '12px 14px', minWidth: '220px', textAlign: 'center' }}>Payload / Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMessages.map((msg, index) => {
                  const statusInfo = deliveryStatusMap[msg.message_deliver] || {
                    label: `Status (${msg.message_deliver ?? 'N/A'})`,
                    bg: '#f1f5f9',
                    text: '#475569',
                    border: '#cbd5e1'
                  };

                  return (
                    <tr
                      key={msg.message_id ? `${msg.message_id}_${index}` : index}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        transition: 'background 0.15s',
                        background: index % 2 === 0 ? '#ffffff' : '#fafafa'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = '#f0fdf4'}
                      onMouseOut={(e) => e.currentTarget.style.background = index % 2 === 0 ? '#ffffff' : '#fafafa'}
                    >
                      {/* # Index */}
                      <td style={{ padding: '10px 14px', textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>
                        {index + 1}
                      </td>

                      {/* Phone */}
                      <td style={{ padding: '10px 14px', fontWeight: 600, color: '#1e293b' }}>
                        <span style={{
                          background: '#f1f5f9',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontFamily: 'monospace',
                          fontSize: '12.5px'
                        }}>
                          +{msg.wa_id || 'N/A'}
                        </span>
                      </td>

                      {/* Message ID */}
                      <td style={{ padding: '10px 14px' }}>
                        <div
                          title={msg.message_id}
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '11.5px',
                            color: '#475569',
                            maxWidth: '220px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {msg.message_id || 'N/A'}
                        </div>
                      </td>

                      {/* Delivery Status Badge */}
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '3px 10px',
                          borderRadius: '20px',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          background: statusInfo.bg,
                          color: statusInfo.text,
                          border: `1px solid ${statusInfo.border}`
                        }}>
                          {statusInfo.label}
                        </span>
                      </td>

                      {/* Date & Time */}
                      <td style={{ padding: '10px 14px', color: '#64748b', fontSize: '12px' }}>
                        <div>{msg.date || 'N/A'}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{msg.time || ''}</div>
                      </td>

                      {/* Two Action Buttons: Show Request & Show Response */}
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex', gap: '8px' }}>
                          {/* Show Request Button */}
                          <button
                            type="button"
                            onClick={() => handleOpenModal(msg, 'Request')}
                            style={{
                              background: '#eff6ff',
                              color: '#1d4ed8',
                              border: '1px solid #bfdbfe',
                              borderRadius: '6px',
                              padding: '5px 10px',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              transition: 'all 0.15s'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.background = '#dbeafe';
                              e.currentTarget.style.borderColor = '#93c5fd';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.background = '#eff6ff';
                              e.currentTarget.style.borderColor = '#bfdbfe';
                            }}
                          >
                            <FontAwesomeIcon icon={faCode} style={{ fontSize: '11px' }} />
                            Show Request
                          </button>

                          {/* Show Response Button */}
                          <button
                            type="button"
                            onClick={() => handleOpenModal(msg, 'Response')}
                            style={{
                              background: '#f0fdf4',
                              color: '#15803d',
                              border: '1px solid #bbf7d0',
                              borderRadius: '6px',
                              padding: '5px 10px',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              transition: 'all 0.15s'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.background = '#dcfce7';
                              e.currentTarget.style.borderColor = '#86efac';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.background = '#f0fdf4';
                              e.currentTarget.style.borderColor = '#bbf7d0';
                            }}
                          >
                            <FontAwesomeIcon icon={faFileCode} style={{ fontSize: '11px' }} />
                            Show Response
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Loading indicator when scrolling or loading more */}
          {loadingMore && (
            <div style={{ textAlign: 'center', padding: '16px', background: '#f8fafc', color: GMB_GREEN, fontWeight: 600, fontSize: '13px' }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
              Loading next 100 messages from server...
            </div>
          )}
        </div>

        {/* Footer Bar with Next Page URL status & Load More trigger */}
        <div style={{
          padding: '10px 18px',
          borderTop: '1px solid #e2e8f0',
          background: '#f8fafc',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px',
          fontSize: '12.5px',
          color: '#64748b',
          flexShrink: 0
        }}>
          <div>
            <span>Showing <strong>{filteredMessages.length}</strong> of <strong>{messages.length}</strong> loaded messages</span>
            {nextPageUrl ? (
              <span style={{ marginLeft: '12px', color: '#16a34a', fontWeight: 600 }}>
                • More messages available on server
              </span>
            ) : (
              <span style={{ marginLeft: '12px', color: '#94a3b8' }}>
                • All messages loaded
              </span>
            )}
          </div>

          {/* Manual Load More Button if next_page_url is available */}
          {hasMore && (
            <button
              type="button"
              onClick={loadNextPage}
              disabled={loadingMore || loading}
              style={{
                background: loadingMore ? '#e2e8f0' : GMB_GREEN,
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 14px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: loadingMore ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 6px rgba(7,94,84,0.2)'
              }}
            >
              <FontAwesomeIcon icon={loadingMore ? faSpinner : faChevronRight} spin={loadingMore} />
              {loadingMore ? 'Loading next 100...' : 'Load Next 100 Messages'}
            </button>
          )}
        </div>
      </div>

      {/* JSON Viewer Modal for Request & Response with Multiple View Modes */}
      {modalData.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.7)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '20px'
          }}
          onClick={handleCloseModal}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              maxWidth: '820px',
              width: '100%',
              maxHeight: '88vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
              overflow: 'hidden',
              animation: 'modalFadeIn 0.2s ease-out',
              border: '1px solid #e2e8f0'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header: Unified WhatsApp Green Theme */}
            <div style={{
              padding: '16px 20px',
              background: `linear-gradient(135deg, ${GMB_GREEN} 0%, ${GMB_GREEN_LIGHT} 100%)`,
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '10px',
                  background: 'rgba(255,255,255,0.18)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '17px'
                }}>
                  <FontAwesomeIcon icon={modalData.type === 'Request' ? faCode : faFileCode} />
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h5 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>
                      {modalData.title}
                    </h5>
                    <span style={{
                      fontSize: '10.5px',
                      fontWeight: 800,
                      padding: '2px 8px',
                      borderRadius: '4px',
                      background: modalData.type === 'Request' ? 'rgba(59, 130, 246, 0.35)' : 'rgba(34, 197, 94, 0.35)',
                      color: '#ffffff',
                      letterSpacing: '0.5px',
                      border: '1px solid rgba(255,255,255,0.25)'
                    }}>
                      {modalData.type.toUpperCase()}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '2px' }}>
                    Phone: <strong>+{modalData.phone}</strong> {modalData.date && `• ${modalData.date} ${modalData.time}`}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleCloseModal}
                style={{
                  background: 'rgba(255,255,255,0.18)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Modal Subheader Bar with json badge & Copy Button */}
            <div style={{
              padding: '10px 20px',
              background: '#27272a',
              borderBottom: '1px solid #3f3f46',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{
                background: '#18181b',
                color: '#a1a1aa',
                padding: '3px 10px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                fontFamily: 'monospace',
                border: '1px solid #3f3f46'
              }}>
                json
              </span>

              {/* Copy Button */}
              <button
                type="button"
                onClick={handleCopyJson}
                style={{
                  background: copied ? '#16a34a' : '#3f3f46',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '5px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }}
                onMouseOver={(e) => { if (!copied) e.currentTarget.style.background = '#52525b'; }}
                onMouseOut={(e) => { if (!copied) e.currentTarget.style.background = '#3f3f46'; }}
              >
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                {copied ? 'Copied!' : 'Copy JSON'}
              </button>
            </div>

            {/* Modal Body: Formatted JSON */}
            <div style={{
              padding: '20px 24px',
              flex: 1,
              overflowY: 'auto',
              background: '#18181b',
              backgroundColor: '#18181b',
              color: '#f8fafc'
            }}>
              <pre
                className="json-modal-pre"
                style={{
                  margin: 0,
                  padding: 0,
                  background: 'transparent',
                  backgroundColor: 'transparent',
                  border: 'none',
                  boxShadow: 'none',
                  color: '#f8fafc',
                  fontFamily: '"Fira Code", "Cascadia Code", "JetBrains Mono", Consolas, monospace',
                  fontSize: '13.5px',
                  lineHeight: '1.65',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all'
                }}
                dangerouslySetInnerHTML={{ __html: highlightJson(modalData.formattedStr) }}
              />
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '12px 20px',
              background: '#27272a',
              borderTop: '1px solid #3f3f46',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '11.5px', color: '#a1a1aa' }}>
                <span style={{ color: '#f43f68', fontWeight: 700 }}>■</span> Keys &nbsp;
                <span style={{ color: '#bef264', fontWeight: 700 }}>■</span> Strings &nbsp;
                <span style={{ color: '#818cf8', fontWeight: 700 }}>■</span> Booleans &nbsp;
                <span style={{ color: '#fb923c', fontWeight: 700 }}>■</span> Numbers
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                style={{
                  background: GMB_GREEN,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '7px 20px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(7,94,84,0.25)'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = GMB_GREEN_LIGHT}
                onMouseOut={(e) => e.currentTarget.style.background = GMB_GREEN}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animation & Scoped Modal Styles */}
      <style>{`
        @keyframes modalFadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        pre.json-modal-pre {
          background-color: transparent !important;
          background: transparent !important;
          border: none !important;
          color: #f8fafc !important;
        }
      `}</style>
    </div>
  );
};

export default SendingMessages;